<template>
    <BaseField
        :label="label"
        :placeholder="placeholder"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        data-spec-class="field-textarea"
    >
        <div :class="['relative rounded-md', wrapperClassAddition]">
            <textarea
                v-bind="$attrs"
                ref="textarea"
                :rows="rows"
                :name="name"
                :class="classObject"
                :placeholder="placeholder"
                :disabled="isDisabled"
                :value="modelValue"
                :data-spec-class="dataSpecClass"
                @input="$emit('update:modelValue', $event.target.value)"
                @blur="hasAutoDirty ? vuelidateField?.$touch() : () => {}"
            />

            <div
                v-if="errorMessagesTotal.length || !!$slots.addOn"
                class="absolute inset-y-0 right-0 flex items-center pr-3"
            >
                <ExclamationCircleIcon
                    v-if="errorMessagesTotal.length"
                    class="h-5 w-5 text-danger"
                    aria-hidden="true"
                />

                <span
                    v-if="!!$slots.addOn"
                    class="ml-1 text-tertiary sm:text-sm"
                >
                    <slot name="addOn" />
                </span>
            </div>
        </div>
    </BaseField>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ExclamationCircleIcon } from '@heroicons/vue/20/solid';
import { Theme } from '@/types';
import BaseField from './BaseField.vue';
import { useField } from '@/composables/field';

defineEmits(['update:modelValue']);

const { normalThemeFieldClasses, errorThemeFieldClasses, disabledThemeFieldClasses } = useField();

const textarea = ref(null);

function focus() {
    textarea.value.focus();
}

function select() {
    textarea.value.select();
}

defineExpose({ focus, select });

interface Props {
    name: string;
    rows?: number;
    label?: string;
    hint?: string;
    placeholder?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    modelValue?: string;
    vuelidateField?: unknown;
    errorMessages?: string[];
    wrapperClassAddition?: string;
    customThemeBase?: string;
    customThemeNormal?: string;
    dataSpecClass?: string;
    hasAutoDirty?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    rows: 3,
    placeholder: '',
    isOptional: false,
    isDisabled: false,
    modelValue: undefined,
    vuelidateField: undefined,
    errorMessages: () => [],
    wrapperClassAddition: undefined,
    customThemeBase: undefined,
    customThemeNormal: undefined,
    dataSpecClass: 'field-textarea__textarea',
    hasAutoDirty: true,
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => [...vuelidateErrors.value, ...props.errorMessages]);

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }

    return Theme.Normal;
});

const themeBase = computed(() => {
    const defaultThemeBase = 'block w-full rounded-md shadow-xs sm:text-sm';
    return typeof props.customThemeBase === 'string' ? props.customThemeBase : defaultThemeBase;
});

const themeNormal = computed(() => {
    return typeof props.customThemeNormal === 'string'
        ? props.customThemeNormal
        : normalThemeFieldClasses;
});

const classObject = computed(() => ({
    'transition-ease': true,
    [themeBase.value]: true,
    [themeNormal.value]: theme.value === Theme.Normal,
    [errorThemeFieldClasses]: theme.value === Theme.Danger,
    [disabledThemeFieldClasses]: theme.value === Theme.Disabled,
}));
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
